import React, { FormEvent, KeyboardEvent, useEffect, useRef } from "react";
import send from "../assets/send.svg";

type ChatComposerProps = {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  disabled?: boolean;
  busy?: boolean;
  placeholder?: string;
  assistantName?: string;
  inputRef?: React.RefObject<HTMLTextAreaElement>;
  buttonText?: string;
  helpText?: string;
};

const ChatComposer = ({
  value,
  onChange,
  onSubmit,
  disabled = false,
  busy = false,
  placeholder = "Message the assistant",
  assistantName = "assistant",
  inputRef,
  buttonText,
  helpText = "Press Enter to send. Shift + Enter adds a new line.",
}: ChatComposerProps) => {
  const localRef = useRef<HTMLTextAreaElement>(null);
  const textareaRef = inputRef || localRef;

  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 160)}px`;
  }, [value, textareaRef]);

  const submit = (event?: FormEvent) => {
    event?.preventDefault();
    if (!disabled && !busy && value.trim()) {
      textareaRef.current?.focus();
      onSubmit();
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
      event.preventDefault();
      submit();
    }
  };

  return (
    <form
      className="chat-input-form"
      onSubmit={submit}
      aria-label={`Message ${assistantName}`}
      aria-busy={busy}
    >
      <label className="visually-hidden" htmlFor="agent-chat-input">
        Type your message to {assistantName}
      </label>
      <div className="chat-input">
        <textarea
          id="agent-chat-input"
          ref={textareaRef}
          rows={1}
          placeholder={placeholder}
          onChange={(event) => onChange(event.target.value)}
          onKeyDown={handleKeyDown}
          value={value}
          disabled={disabled}
          readOnly={busy}
          aria-busy={busy}
          aria-describedby={helpText ? "chat-composer-help" : undefined}
          className="chatbot-input"
        />
        <button
          disabled={disabled || busy || !value.trim()}
          type="submit"
          aria-label={busy ? `${assistantName} is responding` : disabled ? "Messaging unavailable" : "Send message"}
          title="Send message"
        >
          {buttonText ? buttonText : <img src={send} alt="" aria-hidden="true" />}
        </button>
      </div>
      {helpText && (
        <p className="chat-composer-help" id="chat-composer-help">
          {helpText}
        </p>
      )}
    </form>
  );
};

export default ChatComposer;
